import React, { createContext, Suspense, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { onboardingApi, setupApi } from '../../api/services';
import type { ClaudePlan, SetupSource, SetupStatus, SetupStepId } from '../../types/models';
import { useToast } from '@/components/ui/toast';
import { setupCopy } from '../../copy/setup';
import { SETUP_STATUS_EVENT } from './setupParts';
import { trackSetup } from './telemetry';

// Guided setup, part two (GUIDED_SETUP_BUILD_SPEC §2–§4): one place that knows
// what Cadence has seen of the person's Claude setup, keeps it current, and
// owns the four step drawers so the Dashboard card, the rail tile and a
// `?setup=next` link all open the same one.
//
// Freshness: the server nudges this person's apps over SignalR the moment a
// step's event lands (AppLayout turns it into SETUP_STATUS_EVENT, and sends one
// on reconnect). Polling is only the fallback: every 15 seconds while a drawer
// waits on Claude (for up to 10 minutes, until Claude answers), every 5 minutes
// while the checklist is on screen, never once setup is done or out of sight.

export { SETUP_STATUS_EVENT };
export const SETUP_STEPS: SetupStepId[] = ['connector', 'skills', 'tools', 'routines'];
/** Minutes each step takes, for "about n minutes left" (spec §2.1). */
export const STEP_MINUTES: Record<SetupStepId, number> = { connector: 2, skills: 3, tools: 2, routines: 3 };

const IDLE_POLL_MS = 5 * 60_000;
const WATCHING_POLL_MS = 15_000;
/** How long a drawer keeps watching for Claude after the person's last copy or open. */
const WATCH_FOR_MS = 10 * 60_000;
/** A check Claude runs arrives within a minute; after this, the drawer says what usually went wrong. */
const WATCH_SLOW_MS = 2 * 60_000;
/** Nudges closer together than this share one fetch. */
const NUDGE_GAP_MS = 5_000;
/** Coming back to the tab refetches, while setup is on screen, when the last fetch is older than this. */
const STALE_AFTER_MS = 5 * 60_000;

const SetupDrawer = lazy(() => import('./SetupDrawer'));

interface SetupContextValue {
  status: SetupStatus | null;
  /** The step drawer that's open, if any. */
  openStepId: SetupStepId | null;
  /** Steps the person has just started in Claude and Cadence hasn't heard back about yet. */
  watching: Partial<Record<SetupStepId, boolean>>;
  /** Watched steps still waiting after two minutes: Claude most likely answered without the skills. */
  slow: Partial<Record<SetupStepId, boolean>>;
  nextStep: SetupStepId | null;
  doneCount: number;
  openStep: (step: SetupStepId | 'next') => void;
  closeStep: () => void;
  /** Start (or restart) watching a step: the person copied a field or opened Claude. */
  watch: (step: SetupStepId) => void;
  refresh: () => Promise<void>;
  hideCard: () => Promise<void>;
  /** False (after saying so) when the save failed. */
  showCard: () => Promise<boolean>;
  setSources: (sources: SetupSource[]) => Promise<void>;
  setAssistant: (aiClient: 'claude') => Promise<void>;
  /** "How do you have Claude?", when it wasn't asked (switched to Claude later). */
  setClaudePlan: (plan: ClaudePlan) => Promise<void>;
}

const SetupContext = createContext<SetupContextValue | null>(null);

export function useSetup(): SetupContextValue | null {
  return useContext(SetupContext);
}

/** What changes when Claude answers a step, so a watch can tell a fresh answer from an old one without comparing clocks. */
function answerMark(step: SetupStepId, status: SetupStatus | null): string {
  if (!status) return '';
  switch (step) {
    case 'connector': return String(!!status.steps.connector?.done);
    case 'skills': return `${status.skills.checkedAt ?? ''}|${!!status.steps.skills?.done}`;
    case 'tools': return Object.values(status.sources).map((s) => `${s.checkedAt ?? ''}:${s.status}`).join(',') + `|${!!status.steps.tools?.done}`;
    case 'routines': return Object.values(status.routines).map((r) => r.lastRunAt ?? '').join(',');
  }
}

function minutesTotal(status: SetupStatus): number | undefined {
  const times = SETUP_STEPS.map((s) => status.steps[s]?.at).filter((t): t is string => !!t).map((t) => Date.parse(t)).filter((t) => !Number.isNaN(t));
  return times.length ? Math.round((Math.max(...times) - Math.min(...times)) / 60_000) : undefined;
}

function failing(s: { status: string } | undefined): boolean {
  return s?.status === 'missing' || s?.status === 'error';
}

export function SetupProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [openStepId, setOpenStepId] = useState<SetupStepId | null>(null);
  const [watches, setWatches] = useState<Partial<Record<SetupStepId, { mark: string; slow?: boolean }>>>({});
  const [linkRechecked, setLinkRechecked] = useState(false);
  const openedAt = useRef<{ step: SetupStepId; at: number } | null>(null);
  const previous = useRef<SetupStatus | null>(null);
  const statusRef = useRef<SetupStatus | null>(null);
  const seq = useRef(0);
  const lastJson = useRef('');
  const lastFetchAt = useRef(0);
  /** The newest fetch, so a caller can wait until the status is current. */
  const latestFetch = useRef<Promise<void>>(Promise.resolve());
  const trailing = useRef<number | null>(null);
  const watchTimers = useRef<Partial<Record<SetupStepId, number>>>({});
  const slowTimers = useRef<Partial<Record<SetupStepId, number>>>({});
  const linkRecheckStarted = useRef(false);

  // Tenant people only: platform admins have no setup of their own.
  const enabled = !!user && user.role !== 'PlatformAdmin';

  const refresh = useCallback((): Promise<void> => {
    if (!enabled) return Promise.resolve();
    const mine = ++seq.current;
    lastFetchAt.current = Date.now();
    const fetch = (async () => {
      try {
        const next = await setupApi.getStatus();
        if (mine !== seq.current) return;          // a newer fetch has started; it wins
        const json = JSON.stringify(next);
        if (json === lastJson.current) return;     // nothing changed: no re-render
        lastJson.current = json;
        statusRef.current = next;
        setStatus(next);
      } catch {
        /* keep the last known status; the next poll or nudge tries again */
      }
    })();
    latestFetch.current = fetch;
    return fetch;
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live nudges (SignalR, a save elsewhere in the app), coalesced.
  useEffect(() => {
    const handler = () => {
      const since = Date.now() - lastFetchAt.current;
      if (since >= NUDGE_GAP_MS) { refresh(); return; }
      if (trailing.current !== null) return;
      trailing.current = window.setTimeout(() => { trailing.current = null; refresh(); }, NUDGE_GAP_MS - since);
    };
    window.addEventListener(SETUP_STATUS_EVENT, handler);
    return () => {
      window.removeEventListener(SETUP_STATUS_EVENT, handler);
      if (trailing.current !== null) { window.clearTimeout(trailing.current); trailing.current = null; }
    };
  }, [refresh]);

  // A watch ends when Claude answers (the step's mark moves on) or after 10 minutes.
  const { watching, slow } = useMemo(() => {
    const live: Partial<Record<SetupStepId, boolean>> = {};
    const late: Partial<Record<SetupStepId, boolean>> = {};
    for (const step of SETUP_STEPS) {
      const w = watches[step];
      if (w && w.mark === answerMark(step, status)) {
        live[step] = true;
        if (w.slow) late[step] = true;
      }
    }
    return { watching: live, slow: late };
  }, [watches, status]);

  useEffect(() => () => {
    for (const timers of [watchTimers.current, slowTimers.current]) {
      Object.values(timers).forEach((t) => t !== undefined && window.clearTimeout(t));
    }
  }, []);

  // Polling fallback. Fast only while an open drawer waits on Claude for a
  // check or a connection; routine runs come on Claude's schedule, so those
  // rely on the nudge.
  const fastPoll = openStepId !== null && openStepId !== 'routines' && !!watching[openStepId];
  const idlePoll = enabled && (!status
    || (status.aiClient === 'claude' && !status.complete && (!status.cardHidden || openStepId !== null)));
  useEffect(() => {
    if (!fastPoll && !idlePoll) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, fastPoll ? WATCHING_POLL_MS : IDLE_POLL_MS);
    return () => window.clearInterval(id);
  }, [fastPoll, idlePoll, refresh]);

  // Back to the tab after a while, while setup is on screen: catch up.
  useEffect(() => {
    if (!fastPoll && !idlePoll) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchAt.current > STALE_AFTER_MS) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fastPoll, idlePoll, refresh]);

  // Telemetry: how long each step took, failed checks, and the whole setup.
  useEffect(() => {
    const before = previous.current;
    previous.current = status;
    if (!before || !status) return;
    for (const step of SETUP_STEPS) {
      if (!before.steps?.[step]?.done && status.steps?.[step]?.done) {
        const secondsFromOpen = openedAt.current?.step === step ? Math.round((Date.now() - openedAt.current.at) / 1000) : undefined;
        trackSetup('setup_step_detected', { step, secondsFromOpen });
      }
    }
    for (const [source, s] of Object.entries(status.sources ?? {})) {
      const was = before.sources?.[source];
      if (failing(s) && (!failing(was) || was?.checkedAt !== s.checkedAt)) trackSetup('setup_check_failed', { source });
    }
    if (!before.complete && status.complete) {
      trackSetup('setup_completed', { minutesTotal: minutesTotal(status), sources: Object.keys(status.sources).length, plan: status.claudePlan ?? '' });
    }
  }, [status]);

  const nextStep = useMemo<SetupStepId | null>(
    () => SETUP_STEPS.find((s) => !status?.steps?.[s]?.done) ?? null,
    [status],
  );
  const doneCount = useMemo(() => SETUP_STEPS.filter((s) => status?.steps?.[s]?.done).length, [status]);

  const openStep = useCallback((step: SetupStepId | 'next') => {
    const target = step === 'next' ? (nextStep ?? 'connector') : step;
    openedAt.current = { step: target, at: Date.now() };
    setOpenStepId(target);
    trackSetup('setup_drawer_opened', { step: target });
  }, [nextStep]);

  const closeStep = useCallback(() => setOpenStepId(null), []);

  const watch = useCallback((step: SetupStepId) => {
    setWatches((w) => ({ ...w, [step]: { mark: answerMark(step, statusRef.current) } }));
    const timers = watchTimers.current;
    if (timers[step] !== undefined) window.clearTimeout(timers[step]);
    timers[step] = window.setTimeout(() => {
      delete timers[step];
      setWatches((w) => { const { [step]: _gone, ...rest } = w; return rest; });
    }, WATCH_FOR_MS);
    const late = slowTimers.current;
    if (late[step] !== undefined) window.clearTimeout(late[step]);
    late[step] = window.setTimeout(() => {
      delete late[step];
      setWatches((w) => (w[step] ? { ...w, [step]: { ...w[step]!, slow: true } } : w));
    }, WATCH_SLOW_MS);
  }, []);

  // `?setup=next` (from setup's last step, or the emailed link) or `?setup=<step>`.
  // Right after the wizard the status can be a save behind, so a link for
  // someone who isn't (yet) a Claude user refetches once before giving up.
  // The param is then dropped, with `setupLink` in the history state so the
  // home route doesn't bounce an executive to The Brief.
  const wanted = new URLSearchParams(location.search).get('setup');
  useEffect(() => {
    if (!wanted || !enabled || !status) return;
    if (status.aiClient !== 'claude' && !linkRechecked) {
      if (!linkRecheckStarted.current) {
        linkRecheckStarted.current = true;
        // Wait for the newest fetch, not just this one: a nudge's fetch
        // started meanwhile would otherwise leave the old status in place.
        refresh().then(() => latestFetch.current).finally(() => setLinkRechecked(true));
      }
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete('setup');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true, state: { setupLink: true } });
    if (status.aiClient === 'claude') openStep(SETUP_STEPS.includes(wanted as SetupStepId) ? (wanted as SetupStepId) : 'next');
  }, [wanted, enabled, status, linkRechecked, location.search, location.pathname, navigate, openStep, refresh]);

  const patchAndRefresh = useCallback(async (patch: Parameters<typeof onboardingApi.patchState>[0]) => {
    await onboardingApi.patchState(patch);
    await refresh();
  }, [refresh]);

  const hideCard = useCallback(async () => {
    try {
      await patchAndRefresh({ setupCardHidden: true });
      toast({ variant: 'success', message: setupCopy.card.hidden });
    } catch {
      toast({ variant: 'error', message: setupCopy.card.hideFailed });
    }
  }, [patchAndRefresh, toast]);

  const showCard = useCallback(async () => {
    try {
      await patchAndRefresh({ setupCardHidden: false });
      return true;
    } catch {
      toast({ variant: 'error', message: setupCopy.card.saveFailed });
      return false;
    }
  }, [patchAndRefresh, toast]);

  /** Throws on failure so the caller can put its own control back. */
  const setSources = useCallback(async (sources: SetupSource[]) => {
    await patchAndRefresh({ sources });
  }, [patchAndRefresh]);

  const setAssistant = useCallback(async (aiClient: 'claude') => {
    try {
      await patchAndRefresh({ aiClient });
      trackSetup('setup_ai_client', { client: aiClient });
    } catch {
      toast({ variant: 'error', message: setupCopy.card.saveFailed });
    }
  }, [patchAndRefresh, toast]);

  const setClaudePlan = useCallback(async (claudePlan: ClaudePlan) => {
    try {
      await patchAndRefresh({ claudePlan });
    } catch {
      toast({ variant: 'error', message: setupCopy.card.saveFailed });
    }
  }, [patchAndRefresh, toast]);

  const value = useMemo<SetupContextValue>(() => ({
    status, openStepId, watching, slow, nextStep, doneCount,
    openStep, closeStep, watch, refresh, hideCard, showCard, setSources, setAssistant, setClaudePlan,
  }), [status, openStepId, watching, slow, nextStep, doneCount, openStep, closeStep, watch, refresh, hideCard, showCard, setSources, setAssistant, setClaudePlan]);

  return (
    <SetupContext.Provider value={value}>
      {children}
      {enabled && openStepId && status && (
        <Suspense fallback={null}>
          <SetupDrawer />
        </Suspense>
      )}
    </SetupContext.Provider>
  );
}
